import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Box, Flex, Text, Button, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
    HStack, VStack, useToast, Badge, Spinner, Progress, Input, Select, Checkbox, Switch,
    Tabs, TabList, Tab, TabPanels, TabPanel, SimpleGrid, Stat, StatLabel, StatNumber,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    FormControl, FormLabel, useDisclosure, IconButton, Tooltip, Divider,
    Alert, AlertIcon, AlertTitle, AlertDescription, Tag, TagLabel, TagCloseButton,
} from "@chakra-ui/react";
import { RepeatIcon, DownloadIcon, InfoOutlineIcon } from "@chakra-ui/icons";
import { FaBan, FaPlay, FaSave } from "react-icons/fa";
import { TrendingUp } from "lucide-react";
import {
    precificacaoDashboard, precificacaoGetConfig, precificacaoSaveConfig,
    precificacaoListarFornecedores, precificacaoPreviewExecucao, precificacaoDispatchExecucao,
    precificacaoListarExecucoes, precificacaoStatusExecucao, precificacaoCancelarExecucao,
    precificacaoListarItens, precificacaoResumoExecucao, precificacaoBaixarCsv,
    precificacaoListarEstados,
} from "../services/api";
import ScrollToTop from "../components/ScrollToTop";

const ACCENT = "#F68633";

const NIVEIS = ["novo", "adormecido", "despertando", "constante", "veloz"];
const POSICOES = ["sem_rivais", "contestado", "inviavel", "sem_dados"];
const CANAIS = ["ml_1", "ml_2", "ecommerce"];

const NIVEL_LABEL = {
    novo: "Novo", adormecido: "Adormecido", despertando: "Despertando",
    constante: "Constante", veloz: "Veloz",
};
const NIVEL_COLOR = {
    novo: "cyan", adormecido: "gray", despertando: "yellow", constante: "green", veloz: "purple",
};
const POSICAO_LABEL = {
    sem_rivais: "Sem rivais", contestado: "Contestado", inviavel: "Inviável", sem_dados: "Sem dados",
};
const CANAL_LABEL = { ml_1: "ML Conta 1", ml_2: "ML Conta 2", ecommerce: "E-commerce" };
const ACAO_LABEL = {
    calibrar: "Calibrar", tracionar: "Tracionar", sondar: "Sondar", recompor: "Recompor",
    maximizar: "Maximizar", acompanhar: "Acompanhar", extrair: "Extrair",
    extrair_cauteloso: "Extrair cauteloso", proteger: "Proteger", defender: "Defender",
    manter_mc: "Manter MC", mc_ref: "MC ref", mc_ref_observar: "MC ref (observar)",
    liquidar: "Liquidar",
};
const STATUS_EXEC = {
    pendente: { label: "Pendente", color: "gray" },
    em_processamento: { label: "Rodando", color: "blue" },
    concluido: { label: "Concluído", color: "green" },
    falhou: { label: "Falhou", color: "red" },
    cancelado: { label: "Cancelado", color: "orange" },
};

const fmtBRL = (v) => (v === null || v === undefined) ? "—"
    : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtPct = (v) => (v === null || v === undefined) ? "—" : `${(Number(v) * 100).toFixed(1)}%`;
const fmtInt = (v) => (v === null || v === undefined) ? "0" : Number(v).toLocaleString("pt-BR");
const fmtData = (v) => v ? new Date(String(v).replace(" ", "T")).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—";
const minutosDesde = (v) => v ? Math.floor((Date.now() - new Date(String(v).replace(" ", "T")).getTime()) / 60000) : null;
const ROTA_LABEL = {
    via_queima_estoque: "queima de estoque", pulado_promo_lista: "lista de desconto", pulado_promo_ml: "promoção ML",
};

const StatCard = ({ label, value, help, color }) => (
    <Box bg="white" borderRadius="md" boxShadow="sm" p={3}>
        <Stat>
            <StatLabel fontSize="xs" color="gray.500">{label}</StatLabel>
            <StatNumber fontSize="xl" color={color || "gray.800"}>{value}</StatNumber>
            {help && <Text fontSize="xs" color="gray.400">{help}</Text>}
        </Stat>
    </Box>
);

// ─────────────────────────── Detalhe / progresso de execução ───────────────────────────

const ExecucaoDetalhe = ({ execucaoId, onAtualizada }) => {
    const toast = useToast();
    const [status, setStatus] = useState(null);
    const [resumo, setResumo] = useState(null);
    const [itens, setItens] = useState(null);
    const [pagina, setPagina] = useState(1);
    const FILTROS_PADRAO = { canal: "", nivel: "", posicao: "", acao: "", mudou_preco: false, no_ar: false, tipo_mudanca: "", busca: "" };
    const [filtros, setFiltros] = useState(FILTROS_PADRAO);
    const [erroStatus, setErroStatus] = useState(false);
    const [buscaInput, setBuscaInput] = useState("");
    const pollingRef = useRef(null);
    const finalizada = status && ["concluido", "falhou", "cancelado"].includes(status.status);

    const carregarStatus = useCallback(async () => {
        try {
            const r = await precificacaoStatusExecucao(execucaoId);
            setStatus(r.data);
            setErroStatus(false);
            if (["concluido", "falhou", "cancelado"].includes(r.data.status)) {
                if (pollingRef.current) {
                    clearInterval(pollingRef.current);
                    pollingRef.current = null;
                    onAtualizada && onAtualizada();
                }
            }
        } catch { setErroStatus(true); }
    }, [execucaoId, onAtualizada]);

    const carregarResumo = useCallback(async () => {
        try {
            const r = await precificacaoResumoExecucao(execucaoId);
            setResumo(r.data.resumo);
        } catch { /* silencioso */ }
    }, [execucaoId]);

    const carregarItens = useCallback(async (pag = 1, filtrosAtuais = null) => {
        const f = filtrosAtuais || filtros;
        try {
            const params = { page: pag, per_page: 25 };
            if (f.canal) params.canal = f.canal;
            if (f.nivel) params.nivel = f.nivel;
            if (f.posicao) params.posicao = f.posicao;
            if (f.acao) params.acao = f.acao;
            if (f.mudou_preco) params.mudou_preco = 1;
            if (f.no_ar) params.no_ar = 1;
            if (f.tipo_mudanca) params.tipo_mudanca = f.tipo_mudanca;
            if (f.busca) params.busca = f.busca;
            const r = await precificacaoListarItens(execucaoId, params);
            setItens(r.data);
            setPagina(pag);
        } catch { /* silencioso */ }
    }, [execucaoId, filtros]);

    useEffect(() => {
        setStatus(null); setResumo(null); setItens(null); setPagina(1);
        setFiltros(FILTROS_PADRAO); setBuscaInput(""); setErroStatus(false);
        carregarStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [execucaoId]);

    useEffect(() => {
        if (!status) return;
        const rodando = ["pendente", "em_processamento"].includes(status.status);
        if (rodando && !pollingRef.current) {
            pollingRef.current = setInterval(carregarStatus, 4000);
        }
        if (!rodando) {
            if (!resumo) carregarResumo();
            if (!itens) carregarItens(1);
        }
        return () => {
            if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    const handleCancelar = async () => {
        if (!window.confirm("Cancelar esta execução?")) return;
        try {
            await precificacaoCancelarExecucao(execucaoId);
            toast({ title: "Execução cancelada", status: "info", duration: 3000 });
            carregarStatus();
        } catch (e) {
            toast({ title: "Erro ao cancelar", description: e.response?.data?.error, status: "error", duration: 4000 });
        }
    };

    const aplicarFiltros = (novos) => {
        const f = { ...filtros, ...novos };
        setFiltros(f);
        carregarItens(1, f);
    };

    if (!status && erroStatus) {
        return (
            <Alert status="error" borderRadius="md" py={2} fontSize="sm">
                <AlertIcon />
                <AlertDescription flex="1">Não foi possível carregar a execução #{execucaoId}.</AlertDescription>
                <Button size="xs" onClick={carregarStatus}>Tentar de novo</Button>
            </Alert>
        );
    }
    if (!status) return <Flex justify="center" py={6}><Spinner color="orange.500" /></Flex>;

    const rodando = ["pendente", "em_processamento"].includes(status.status);
    const pct = status.total_itens > 0
        ? Math.min(100, Math.round((status.itens_processados / status.total_itens) * 100))
        : (rodando ? 0 : 100);
    const st = STATUS_EXEC[status.status] || { label: status.status, color: "gray" };
    const gradeResumo = resumo?.grade || {};
    const semProgressoMin = rodando ? minutosDesde(status.updated_at) : null;
    const iriamAoAr = (resumo?.por_canal || []).reduce((s, c) => s + Number(c.mudariam_no_ar || 0), 0);

    return (
        <Box>
            <Flex justify="space-between" align="center" mb={2} wrap="wrap" gap={2}>
                <HStack wrap="wrap" spacing={2} rowGap={1}>
                    <Badge colorScheme={st.color}>{st.label}{rodando && <Spinner size="xs" ml={1} />}</Badge>
                    <Text fontSize="sm" fontWeight="semibold">Execução #{execucaoId}</Text>
                    <Badge variant="outline" colorScheme={status.simulacao ? "purple" : "blue"}>
                        {status.simulacao ? "Simulação" : "Oficial"}
                    </Badge>
                    {status.aplicar ? <Badge colorScheme="red">APLICA PREÇOS</Badge>
                        : <Badge colorScheme="green" variant="subtle">Dry-run — nenhum preço alterado</Badge>}
                    <Text fontSize="xs" color="gray.500">
                        início {fmtData(status.iniciado_em)}{status.finalizado_em ? ` · fim ${fmtData(status.finalizado_em)}` : ""}
                    </Text>
                </HStack>
                <HStack>
                    {rodando && (
                        <Button size="xs" leftIcon={<FaBan />} colorScheme="red" variant="outline" onClick={handleCancelar}>
                            Cancelar
                        </Button>
                    )}
                    {finalizada && (
                        <Button size="xs" leftIcon={<DownloadIcon />} variant="outline"
                            onClick={() => precificacaoBaixarCsv(execucaoId).catch(() =>
                                toast({ title: "Erro ao baixar CSV", status: "error", duration: 3000 }))}>
                            CSV
                        </Button>
                    )}
                    <IconButton size="xs" icon={<RepeatIcon />} aria-label="Atualizar"
                        onClick={() => { carregarStatus(); if (finalizada) { carregarResumo(); carregarItens(pagina); } }} />
                </HStack>
            </Flex>

            <Box mb={3}>
                <Flex justify="space-between" mb={1}>
                    <Text fontSize="xs" color="gray.500">
                        {fmtInt(status.itens_processados)} / {fmtInt(status.total_itens)} produtos
                    </Text>
                    <Text fontSize="xs" fontWeight="semibold" color={rodando ? "blue.600" : `${st.color}.600`}>{pct}%</Text>
                </Flex>
                <Progress value={pct} size="sm" colorScheme={rodando ? "blue" : st.color}
                    borderRadius="full" hasStripe={rodando} isAnimated={rodando} />
                {semProgressoMin !== null && semProgressoMin >= 10 && (
                    <Text fontSize="xs" color="orange.600" mt={1}>
                        Sem progresso há {semProgressoMin} min. Se o worker da fila "precificacao" caiu, a execução
                        é dada como falha após 2 h — dá para cancelar agora e rodar de novo.
                    </Text>
                )}
            </Box>

            <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={2} mb={3}>
                <StatCard label="Produtos" value={fmtInt(status.itens_processados)} help="cada um avaliado nos canais" />
                <StatCard label="Mudanças de nível" value={fmtInt(status.itens_mudanca_nivel)} color="blue.600" help="itens (produto×canal)" />
                <StatCard label="Mudariam de preço" value={fmtInt(status.itens_mudanca_preco)} color="orange.600" help="inclui queima/promoção/lista" />
                <StatCard label="Iriam ao ar" value={finalizada && resumo ? fmtInt(iriamAoAr) : "—"} color="orange.700"
                    help="sem queima, promoção ou lista" />
                <StatCard label="Aplicados" value={fmtInt(status.itens_aplicados)} color="green.600" />
                <StatCard label="Erros" value={fmtInt(status.itens_com_erro)} color="red.600" />
            </SimpleGrid>

            {status.erro_msg && (
                <Alert status="error" borderRadius="md" mb={3} py={2}>
                    <AlertIcon boxSize={4} />
                    <AlertDescription fontSize="xs" fontFamily="mono">{status.erro_msg}</AlertDescription>
                </Alert>
            )}

            {finalizada && resumo && (
                <Box mb={4}>
                    <Text fontSize="sm" fontWeight="semibold" mb={2}>Grade nível × posição (itens)</Text>
                    <TableContainer bg="white" borderRadius="md" boxShadow="sm">
                        <Table size="sm">
                            <Thead>
                                <Tr>
                                    <Th>Nível</Th>
                                    {POSICOES.map(p => <Th key={p} isNumeric>{POSICAO_LABEL[p]}</Th>)}
                                </Tr>
                            </Thead>
                            <Tbody>
                                {NIVEIS.map(n => (
                                    <Tr key={n}>
                                        <Td><Badge colorScheme={NIVEL_COLOR[n]}>{NIVEL_LABEL[n]}</Badge></Td>
                                        {POSICOES.map(p => (
                                            <Td key={p} isNumeric>{fmtInt(gradeResumo[n]?.[p] || 0)}</Td>
                                        ))}
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>

                    {resumo.por_canal && (
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2} mt={3}>
                            {resumo.por_canal.map(c => (
                                <Box key={c.canal} bg="white" borderRadius="md" boxShadow="sm" p={3}>
                                    <Text fontSize="sm" fontWeight="semibold" mb={1}>{CANAL_LABEL[c.canal] || c.canal}</Text>
                                    <Text fontSize="xs" color="gray.600">
                                        {fmtInt(c.total)} itens · {fmtInt(c.mudaram_preco)} mudariam de preço
                                        (<Text as="span" color="green.600">{fmtInt(c.subiram)} ↑</Text> / <Text as="span" color="red.600">{fmtInt(c.desceram)} ↓</Text>)
                                    </Text>
                                    {(c.mudariam_no_ar !== undefined) && (
                                        <Text fontSize="xs" color="gray.600">
                                            <Text as="span" color="orange.700" fontWeight="semibold">{fmtInt(c.mudariam_no_ar)}</Text> iriam ao ar
                                            {Number(c.sem_cotacao) > 0 && <> · {fmtInt(c.sem_cotacao)} sem cotação de frete/tarifa do ML</>}
                                        </Text>
                                    )}
                                    {(c.mudaram_motor !== undefined) && (
                                        <Text fontSize="xs" color="gray.600">
                                            <Text as="span" color="orange.600" fontWeight="semibold">{fmtInt(c.mudaram_motor)}</Text> decisão do motor ·{" "}
                                            <Text as="span" color="gray.500">{fmtInt(c.mudaram_defasagem)}</Text> defasagem de fórmula
                                        </Text>
                                    )}
                                    <Text fontSize="xs" color="gray.600">
                                        Δ médio: {fmtBRL(c.delta_medio)} · Sem dados de mercado: {fmtInt(c.sem_dados)}
                                    </Text>
                                </Box>
                            ))}
                        </SimpleGrid>
                    )}
                </Box>
            )}

            {finalizada && (
                <Box>
                    <Flex gap={2} mb={2} wrap="wrap" align="center">
                        <Select size="xs" w="130px" value={filtros.canal} onChange={e => aplicarFiltros({ canal: e.target.value })}>
                            <option value="">Todos canais</option>
                            {CANAIS.map(c => <option key={c} value={c}>{CANAL_LABEL[c]}</option>)}
                        </Select>
                        <Select size="xs" w="130px" value={filtros.nivel} onChange={e => aplicarFiltros({ nivel: e.target.value })}>
                            <option value="">Todos níveis</option>
                            {NIVEIS.map(n => <option key={n} value={n}>{NIVEL_LABEL[n]}</option>)}
                        </Select>
                        <Select size="xs" w="130px" value={filtros.posicao} onChange={e => aplicarFiltros({ posicao: e.target.value })}>
                            <option value="">Todas posições</option>
                            {POSICOES.map(p => <option key={p} value={p}>{POSICAO_LABEL[p]}</option>)}
                        </Select>
                        <Select size="xs" w="150px" value={filtros.acao} onChange={e => aplicarFiltros({ acao: e.target.value })}>
                            <option value="">Todas ações</option>
                            {Object.keys(ACAO_LABEL).map(a => <option key={a} value={a}>{ACAO_LABEL[a]}</option>)}
                        </Select>
                        <Checkbox size="sm" isChecked={filtros.mudou_preco}
                            onChange={e => aplicarFiltros({ mudou_preco: e.target.checked })}>
                            <Text fontSize="xs">Só mudanças de preço</Text>
                        </Checkbox>
                        <Checkbox size="sm" isChecked={filtros.no_ar}
                            onChange={e => aplicarFiltros({ no_ar: e.target.checked })}>
                            <Text fontSize="xs">Só o que iria ao ar</Text>
                        </Checkbox>
                        <Select size="xs" w="170px" value={filtros.tipo_mudanca}
                            onChange={e => aplicarFiltros({ tipo_mudanca: e.target.value })}>
                            <option value="">Mudanças: todas</option>
                            <option value="motor">Decisão do motor (MC mudou)</option>
                            <option value="defasagem">Defasagem de fórmula</option>
                        </Select>
                        <HStack>
                            <Input size="xs" w="180px" placeholder="Buscar código / nome / ID"
                                value={buscaInput} onChange={e => setBuscaInput(e.target.value)}
                                onKeyDown={e => e.key === "Enter" && aplicarFiltros({ busca: buscaInput })} />
                            <Button size="xs" onClick={() => aplicarFiltros({ busca: buscaInput })}>Buscar</Button>
                        </HStack>
                    </Flex>

                    <TableContainer bg="white" borderRadius="md" boxShadow="sm">
                        <Table size="sm">
                            <Thead>
                                <Tr>
                                    <Th>Produto</Th>
                                    <Th>Canal</Th>
                                    <Th>Nível</Th>
                                    <Th>Posição</Th>
                                    <Th>Ação</Th>
                                    <Th isNumeric>MC</Th>
                                    <Th isNumeric>Preço atual</Th>
                                    <Th isNumeric>Preço novo</Th>
                                    <Th isNumeric>Piso</Th>
                                    <Th>Obs</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {(itens?.data || []).map(item => {
                                    const delta = (item.preco_novo !== null && item.preco_atual !== null)
                                        ? item.preco_novo - item.preco_atual : null;
                                    // MC mudou = decisão da grade/escada; MC igual com preço diferente =
                                    // preço publicado defasado da fórmula (ex.: anúncio pausado que o cron não alcança)
                                    const decisaoMotor = item.mudou_preco && Number(item.mc_de) !== Number(item.mc_para);
                                    return (
                                        <Tr key={item.id} bg={item.mudou_preco ? "orange.50" : undefined}>
                                            <Td maxW="240px">
                                                <Text fontSize="xs" fontWeight="semibold" noOfLines={1}>
                                                    {item.codigo_global || item.produto_id}
                                                    <Text as="span" color="gray.400" fontWeight="normal"> · ID {item.produto_id}</Text>
                                                </Text>
                                                <Text fontSize="xs" color="gray.500" noOfLines={1}>{item.produto_nome}</Text>
                                            </Td>
                                            <Td><Text fontSize="xs">{CANAL_LABEL[item.canal]}</Text></Td>
                                            <Td>
                                                <Badge colorScheme={NIVEL_COLOR[item.nivel_para]} fontSize="10px">
                                                    {NIVEL_LABEL[item.nivel_para] || item.nivel_para}
                                                </Badge>
                                                {!!item.mudou_nivel && item.nivel_de && (
                                                    <Text fontSize="10px" color="gray.500">era {NIVEL_LABEL[item.nivel_de] || item.nivel_de}</Text>
                                                )}
                                            </Td>
                                            <Td><Text fontSize="xs">{POSICAO_LABEL[item.posicao] || "—"}</Text></Td>
                                            <Td><Text fontSize="xs">{ACAO_LABEL[item.acao] || item.acao || "—"}</Text></Td>
                                            <Td isNumeric>
                                                <Text fontSize="xs">{fmtPct(item.mc_de)} → <b>{fmtPct(item.mc_para)}</b></Text>
                                            </Td>
                                            <Td isNumeric><Text fontSize="xs">{fmtBRL(item.preco_atual)}</Text></Td>
                                            <Td isNumeric>
                                                <Text fontSize="xs" fontWeight={item.mudou_preco ? "bold" : "normal"}
                                                    color={delta > 0 ? "green.600" : delta < 0 ? "red.600" : undefined}>
                                                    {fmtBRL(item.preco_novo)}
                                                </Text>
                                            </Td>
                                            <Td isNumeric><Text fontSize="xs" color="gray.500">{fmtBRL(item.preco_piso)}</Text></Td>
                                            <Td>
                                                <HStack spacing={1}>
                                                    {item.mudou_preco ? (
                                                        <Tooltip hasArrow fontSize="xs" label={decisaoMotor
                                                            ? "A MC mudou: decisão da grade/escada do motor."
                                                            : "MC igual — o preço publicado está fora da fórmula com o custo atual (ex.: anúncio que os crons não alcançam, margem/config alterada depois)."}>
                                                            <Badge colorScheme={decisaoMotor ? "orange" : "gray"} fontSize="9px" cursor="help">
                                                                {decisaoMotor ? "decisão do motor" : "defasagem"}
                                                            </Badge>
                                                        </Tooltip>
                                                    ) : null}
                                                    {(!!item.mudou_preco && ROTA_LABEL[item.detalhes?.aplicacao]) ? (
                                                        <Badge colorScheme="pink" fontSize="9px">não vai ao ar: {ROTA_LABEL[item.detalhes.aplicacao]}</Badge>
                                                    ) : null}
                                                    {(!item.mudou_preco && item.tem_promocao) ? <Badge colorScheme="pink" fontSize="9px">promo</Badge> : null}
                                                    {(!item.mudou_preco && item.detalhes?.estoque_fisico > 0) ? <Badge colorScheme="teal" fontSize="9px">estoque físico</Badge> : null}
                                                    {item.detalhes?.fonte_preco === "sem_cotacao" ? (
                                                        <Badge colorScheme="yellow" fontSize="9px">sem cotação ML</Badge>
                                                    ) : null}
                                                    {item.status !== "simulado" ? <Badge fontSize="9px">{item.status}</Badge> : null}
                                                </HStack>
                                            </Td>
                                        </Tr>
                                    );
                                })}
                            </Tbody>
                        </Table>
                    </TableContainer>

                    {itens && (
                        <Flex justify="space-between" align="center" mt={2}>
                            <Text fontSize="xs" color="gray.500">
                                {fmtInt(itens.total)} itens · página {itens.current_page} de {itens.last_page}
                            </Text>
                            <HStack>
                                <Button size="xs" isDisabled={pagina <= 1} onClick={() => carregarItens(pagina - 1)}>Anterior</Button>
                                <Button size="xs" isDisabled={itens && pagina >= itens.last_page} onClick={() => carregarItens(pagina + 1)}>Próxima</Button>
                            </HStack>
                        </Flex>
                    )}
                </Box>
            )}
        </Box>
    );
};

// ─────────────────────────── Aba: Visão Geral ───────────────────────────

const VisaoGeralTab = ({ dashboard, onRefresh }) => {
    const [canalGrade, setCanalGrade] = useState("ml_1");

    if (!dashboard) return <Flex justify="center" py={10}><Spinner color="orange.500" /></Flex>;

    const grade = {};
    (dashboard.grade_estados || []).forEach(r => {
        if (r.canal !== canalGrade) return;
        grade[r.nivel] = grade[r.nivel] || {};
        grade[r.nivel][r.posicao || "sem_dados"] = (grade[r.nivel][r.posicao || "sem_dados"] || 0) + Number(r.total);
    });
    const totaisCanal = (dashboard.totais_por_canal || []).find(t => t.canal === canalGrade);
    const temEstados = (dashboard.totais_por_canal || []).length > 0;

    return (
        <Box>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={4}>
                {CANAIS.map(c => {
                    const t = (dashboard.totais_por_canal || []).find(x => x.canal === c);
                    return <StatCard key={c} label={`Classificados · ${CANAL_LABEL[c]}`} value={fmtInt(t?.total || 0)}
                        help={t ? `${fmtInt(t.sem_dados)} sem dados de mercado` : "sem estado vigente"} />;
                })}
                <StatCard label="Cobertura do Comparador" value={fmtInt(dashboard.cobertura_mercado)}
                    help={`produtos com leitura de mercado válida (${dashboard.config?.mercado_validade_dias}d)`} />
            </SimpleGrid>

            {!temEstados && (
                <Alert status="info" borderRadius="md" mb={4} fontSize="sm">
                    <AlertIcon />
                    <AlertDescription>
                        Nenhum nível vigente. Os níveis só passam a valer com o sistema <b>ativo</b>: a execução
                        oficial publica os preços e grava o estado de cada produto×canal. Enquanto estiver desativado,
                        use a aba Simulação para ver a classificação e os preços que o motor daria.
                    </AlertDescription>
                </Alert>
            )}

            <Flex justify="space-between" align="center" mb={2}>
                <Text fontSize="sm" fontWeight="semibold">Grade nível × posição (estados vigentes)</Text>
                <HStack>
                    <Select size="xs" w="140px" value={canalGrade} onChange={e => setCanalGrade(e.target.value)}>
                        {CANAIS.map(c => <option key={c} value={c}>{CANAL_LABEL[c]}</option>)}
                    </Select>
                    <IconButton size="xs" icon={<RepeatIcon />} aria-label="Atualizar" onClick={onRefresh} />
                </HStack>
            </Flex>
            <TableContainer bg="white" borderRadius="md" boxShadow="sm" mb={4}>
                <Table size="sm">
                    <Thead>
                        <Tr>
                            <Th>Nível</Th>
                            {POSICOES.map(p => <Th key={p} isNumeric>{POSICAO_LABEL[p]}</Th>)}
                            <Th isNumeric>Total</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {NIVEIS.map(n => {
                            const linha = grade[n] || {};
                            const total = POSICOES.reduce((s, p) => s + (linha[p] || 0), 0);
                            return (
                                <Tr key={n}>
                                    <Td><Badge colorScheme={NIVEL_COLOR[n]}>{NIVEL_LABEL[n]}</Badge></Td>
                                    {POSICOES.map(p => <Td key={p} isNumeric>{fmtInt(linha[p] || 0)}</Td>)}
                                    <Td isNumeric fontWeight="bold">{fmtInt(total)}</Td>
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>
            </TableContainer>
            {totaisCanal && (
                <Text fontSize="xs" color="gray.500">
                    "% Sem dados" é o KPI de saúde da camada de mercado — deve encolher conforme o
                    Comparador de Preços ganhar cobertura ({fmtInt(totaisCanal.sem_dados)} de {fmtInt(totaisCanal.total)} neste canal).
                </Text>
            )}
        </Box>
    );
};

// ─────────────────────────── Aba: Simulação ───────────────────────────

const SimulacaoTab = ({ config, execucaoSelecionada, setExecucaoSelecionada, onExecucaoDisparada }) => {
    const toast = useToast();
    const [fornecedores, setFornecedores] = useState([]);
    const [form, setForm] = useState({
        fornecedores_ids: [], amostra: "", somente_com_estoque: false, oficial: false,
    });
    const [fornSelect, setFornSelect] = useState("");
    const [preview, setPreview] = useState(null);
    const [carregandoPreview, setCarregandoPreview] = useState(false);
    const [disparando, setDisparando] = useState(false);

    useEffect(() => {
        precificacaoListarFornecedores().then(r => setFornecedores(r.data)).catch(() => { });
    }, []);

    const montarFiltros = () => {
        const filtros = {};
        if (form.fornecedores_ids.length) filtros.fornecedores_ids = form.fornecedores_ids.map(f => f.id);
        if (form.amostra) filtros.amostra_quantidade = Number(form.amostra);
        if (form.somente_com_estoque) filtros.somente_com_estoque = true;
        return filtros;
    };

    const handlePreview = async () => {
        setCarregandoPreview(true);
        try {
            const r = await precificacaoPreviewExecucao({ filtros: montarFiltros() });
            setPreview(r.data);
        } catch (e) {
            toast({ title: "Erro no preview", description: e.response?.data?.error, status: "error", duration: 4000 });
        }
        setCarregandoPreview(false);
    };

    const oficial = !!config?.ativo && form.oficial;

    const handleDispatch = async () => {
        if (oficial && !window.confirm(
            "Execução OFICIAL com o sistema ATIVO: os preços calculados serão PUBLICADOS no Mercado Livre "
            + "(por conta) e no site (sync das 14:25), e o estado do motor será gravado. Continuar?")) return;
        setDisparando(true);
        try {
            const r = await precificacaoDispatchExecucao({
                simulacao: !oficial,
                filtros: montarFiltros(),
            });
            toast({
                title: `Execução #${r.data.execucao_id} disparada!`,
                description: oficial
                    ? "Execução oficial em andamento: os preços serão publicados ao final da avaliação."
                    : "Simulação em andamento — acompanhe o progresso abaixo. Nenhum preço será alterado.",
                status: "success", duration: 5000,
            });
            setExecucaoSelecionada(r.data.execucao_id);
            onExecucaoDisparada && onExecucaoDisparada();
        } catch (e) {
            const validacao = e.response?.data?.errors ? Object.values(e.response.data.errors).flat().join(" ") : null;
            toast({
                title: "Erro ao disparar",
                description: e.response?.data?.error || validacao || e.response?.data?.message
                    || "Verifique se o worker da fila 'precificacao' está rodando.",
                status: "error", duration: 6000,
            });
        }
        setDisparando(false);
    };

    const addFornecedor = (id) => {
        const f = fornecedores.find(x => String(x.id) === String(id));
        if (f && !form.fornecedores_ids.some(x => x.id === f.id)) {
            setForm(v => ({ ...v, fornecedores_ids: [...v.fornecedores_ids, f] }));
        }
        setFornSelect("");
    };

    return (
        <Box>
            <Box bg="white" borderRadius="md" boxShadow="sm" p={4} mb={4}>
                <Text fontSize="sm" fontWeight="semibold" mb={3}>Nova execução</Text>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={3}>
                    <FormControl>
                        <FormLabel fontSize="xs">Fornecedores (vazio = todos)</FormLabel>
                        <Select size="sm" placeholder="Adicionar fornecedor..." value={fornSelect}
                            onChange={e => addFornecedor(e.target.value)}>
                            {fornecedores.map(f => <option key={f.id} value={f.id}>{f.nome || `Fornecedor ${f.id}`}</option>)}
                        </Select>
                        <HStack mt={2} wrap="wrap" spacing={1}>
                            {form.fornecedores_ids.map(f => (
                                <Tag key={f.id} size="sm" colorScheme="orange" borderRadius="full" m={0.5}>
                                    <TagLabel>{f.nome || f.id}</TagLabel>
                                    <TagCloseButton onClick={() => setForm(v => ({
                                        ...v, fornecedores_ids: v.fornecedores_ids.filter(x => x.id !== f.id),
                                    }))} />
                                </Tag>
                            ))}
                        </HStack>
                    </FormControl>
                    <FormControl>
                        <FormLabel fontSize="xs">Amostra aleatória (vazio = catálogo inteiro)</FormLabel>
                        <Input size="sm" type="number" placeholder="ex.: 5000" value={form.amostra}
                            onChange={e => setForm(v => ({ ...v, amostra: e.target.value }))} />
                    </FormControl>
                    <VStack align="flex-start" spacing={2} pt={5}>
                        <Checkbox size="sm" isChecked={form.somente_com_estoque}
                            onChange={e => setForm(v => ({ ...v, somente_com_estoque: e.target.checked }))}>
                            <Text fontSize="xs">Só produtos com estoque</Text>
                        </Checkbox>
                        <Checkbox size="sm" isChecked={oficial} isDisabled={!config?.ativo}
                            onChange={e => setForm(v => ({ ...v, oficial: e.target.checked }))}>
                            <Text fontSize="xs" color={oficial ? "red.600" : undefined}>
                                {config?.ativo
                                    ? "Execução oficial — PUBLICA os preços e grava os níveis"
                                    : "Execução oficial (só com o sistema ativo)"}
                            </Text>
                        </Checkbox>
                    </VStack>
                </SimpleGrid>

                <Alert status={config?.ativo ? "warning" : "info"} borderRadius="md" mb={3} py={2} fontSize="xs">
                    <AlertIcon boxSize={4} />
                    <AlertDescription>
                        {config?.ativo
                            ? "Sistema ATIVO — a simulação não altera nada; a execução oficial publica os preços (ML por conta, site no sync das 14:25) e grava o nível de cada produto×canal."
                            : "Sistema DESATIVADO — toda execução é simulação (dry-run): classifica e calcula preços sem alterar nada no ML, no site ou nos níveis."}
                    </AlertDescription>
                </Alert>

                <HStack>
                    <Button size="sm" variant="outline" isLoading={carregandoPreview} onClick={handlePreview}>
                        Calcular prévia
                    </Button>
                    <Button size="sm" colorScheme={oficial ? "red" : "orange"} leftIcon={<FaPlay />} isLoading={disparando} onClick={handleDispatch}>
                        {oficial ? "Rodar oficial (aplica preços)" : "Rodar simulação"}
                    </Button>
                    {preview && (
                        <Text fontSize="xs" color="gray.600">
                            {fmtInt(preview.total_efetivo)} produtos → estimativa <b>~{preview.estimativa_minutos} min</b>
                            {preview.estimativa_minutos > 5 && " (execução longa — acompanhe pelo progresso, dá para cancelar)"}
                        </Text>
                    )}
                </HStack>
            </Box>

            {execucaoSelecionada ? (
                <ExecucaoDetalhe execucaoId={execucaoSelecionada} />
            ) : (
                <Flex justify="center" align="center" h="120px" direction="column" gap={2}>
                    <TrendingUp size={32} color="#CBD5E0" />
                    <Text color="gray.400" fontSize="sm">Dispare uma simulação ou selecione uma execução na aba Execuções.</Text>
                </Flex>
            )}
        </Box>
    );
};

// ─────────────────────────── Aba: Configurações ───────────────────────────

// tipo: pp = fração exibida em % | pct = já é 0-100 | money | int | bool
const SECOES_CONFIG = [
    {
        titulo: "Margens (camada de custo)",
        campos: [
            { key: "mc_ref", label: "MC de referência (%) — só reserva", tipo: "pp", hint: "o motor usa a margem alvo de cada produto (marca > fornecedor > global, a mesma da produção); este valor só limita a MC mínima" },
            { key: "mc_min", label: "MC mínima / piso (%)", tipo: "pp", hint: "nenhuma ação da grade fura este piso" },
        ],
    },
    {
        titulo: "Níveis de demanda",
        campos: [
            { key: "novo_dias", label: "Novo: dias de exposição", tipo: "int" },
            { key: "adormecido_dias", label: "Adormecido: dias sem venda", tipo: "int" },
            { key: "constante_janela_dias", label: "Constante: janela (dias)", tipo: "int" },
            { key: "constante_janelas", label: "Constante: nº de janelas", tipo: "int" },
            { key: "veloz_top_pct", label: "Veloz: top % de velocidade", tipo: "pct" },
            { key: "veloz_min_und_semana", label: "Veloz: mín. und/semana (opcional)", tipo: "num" },
            { key: "persistencia_avaliacoes", label: "Persistência (avaliações)", tipo: "int" },
        ],
    },
    {
        titulo: "Grade de ações (pp de MC)",
        campos: [
            { key: "calibrar_entrada_pp", label: "Calibrar: entrada (+pp)", tipo: "pp" },
            { key: "calibrar_degrau_pp", label: "Calibrar: degrau (pp)", tipo: "pp" },
            { key: "calibrar_ciclo_dias", label: "Calibrar: ciclo (dias)", tipo: "int" },
            { key: "sondar_degrau_pp", label: "Sondar: degrau (pp)", tipo: "pp" },
            { key: "sondar_ciclo_dias", label: "Sondar: ciclo (dias)", tipo: "int" },
            { key: "recompor_pp", label: "Recompor: degrau (+pp)", tipo: "pp" },
            { key: "recompor_ciclo_dias", label: "Recompor: ciclo (dias)", tipo: "int" },
            { key: "maximizar_pp", label: "Maximizar: acima da ref (+pp)", tipo: "pp" },
            { key: "acompanhar_teto_pp", label: "Acompanhar: teto da banda (+pp)", tipo: "pp" },
            { key: "extrair_pp", label: "Extrair: degrau (+pp)", tipo: "pp" },
            { key: "extrair_ciclo_dias", label: "Extrair: ciclo (dias)", tipo: "int" },
            { key: "extrair_semdados_pp", label: "Extrair s/ dados: degrau (+pp)", tipo: "pp" },
            { key: "extrair_queda_pct", label: "Extrair: sinal de queda (%)", tipo: "pct" },
            { key: "tracionar_folga_pct", label: "Tracionar/Acompanhar: folga sob a oferta (%)", tipo: "pct" },
        ],
    },
    {
        titulo: "Camada de mercado",
        campos: [
            { key: "mercado_ativo", label: "Camada de mercado ligada (usa resultados do Comparador/Serper)", tipo: "bool", hint: "desligada, toda posição vira 'Sem dados' e o motor navega só pela própria venda — nada do Comparador é consumido" },
            { key: "mercado_validade_dias", label: "Validade da leitura do Comparador (dias)", tipo: "int", hint: "resultado mais velho que isso vira 'Sem dados'" },
        ],
    },
    {
        titulo: "Liquidar (devoluções em estoque físico)",
        campos: [
            { key: "liquidar_ativo", label: "Escada do Liquidar ligada (no motor/dry-run)", tipo: "bool" },
            { key: "liquidar_substitui_queima", label: "Substituir a queima fixa de 25% pela escada (só vale com o sistema ATIVO)", tipo: "bool", destaque: true },
            { key: "liquidar_degrau_pp", label: "Degrau (pp)", tipo: "pp" },
            { key: "liquidar_ciclo_dias", label: "Ciclo (dias)", tipo: "int" },
            { key: "liquidar_custo_dias", label: "Chega ao custo aos (dias)", tipo: "int" },
            { key: "liquidar_negativo_dias", label: "Degrau negativo aos (dias)", tipo: "int" },
            { key: "liquidar_mc_final", label: "MC final (%)", tipo: "pp" },
            { key: "liquidar_piso_recuperacao_pct", label: "Piso de recuperação (% do custo)", tipo: "pct" },
        ],
    },
    {
        titulo: "Aplicação (quando o sistema for ativado)",
        campos: [
            { key: "tolerancia_preco", label: "Tolerância de preço (R$)", tipo: "money", hint: "diferença menor que isso não conta como mudança" },
            { key: "max_alteracoes_por_execucao", label: "Máx. de alterações por execução", tipo: "int" },
        ],
    },
    {
        titulo: "Automação",
        campos: [
            { key: "avaliacao_automatica", label: "Avaliação automática semanal", tipo: "bool" },
            { key: "avaliacao_dia_semana", label: "Dia da semana", tipo: "dia" },
            { key: "avaliacao_hora", label: "Hora (0-23)", tipo: "int" },
            { key: "retencao_execucoes", label: "Execuções mantidas no histórico", tipo: "int" },
        ],
    },
];

const DIAS_SEMANA = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

const ConfiguracoesTab = ({ config, onSalvo }) => {
    const toast = useToast();
    const [valores, setValores] = useState(null);
    const [inicial, setInicial] = useState(null);
    const [salvando, setSalvando] = useState(false);
    const { isOpen: confirmAtivarOpen, onOpen: abrirConfirmAtivar, onClose: fecharConfirmAtivar } = useDisclosure();
    const { isOpen: reavaliarOpen, onOpen: abrirReavaliar, onClose: fecharReavaliar } = useDisclosure();
    const [disparandoReavaliacao, setDisparandoReavaliacao] = useState(false);

    useEffect(() => {
        if (config) {
            const v = JSON.parse(JSON.stringify(config));
            setValores(v);
            setInicial(JSON.stringify(v));
        }
    }, [config]);

    if (!valores) return <Flex justify="center" py={10}><Spinner color="orange.500" /></Flex>;

    const hasChanges = JSON.stringify(valores) !== inicial;

    const displayValor = (campo) => {
        const v = valores[campo.key];
        if (v === null || v === undefined) return "";
        if (campo.tipo === "pp") return String(Math.round(Number(v) * 10000) / 100);
        return String(v);
    };

    const setCampo = (campo, raw) => {
        setValores(prev => {
            const novo = { ...prev };
            if (raw === "" || raw === null) {
                novo[campo.key] = null;
            } else if (campo.tipo === "pp") {
                novo[campo.key] = Number(raw) / 100;
            } else if (campo.tipo === "bool") {
                novo[campo.key] = raw;
            } else {
                novo[campo.key] = Number(raw);
            }
            return novo;
        });
    };

    const montarPayload = () => {
        const payload = {};
        SECOES_CONFIG.forEach(sec => sec.campos.forEach(c => {
            let v = valores[c.key];
            if (v === "" || v === undefined) v = null;
            if (v !== null || c.key === "veloz_min_und_semana") payload[c.key] = v;
        }));
        payload.ativo = !!valores.ativo;
        payload.canais = valores.canais || CANAIS;
        return payload;
    };

    const salvar = async (confirmadoAtivar = false) => {
        if (valores.ativo && !config.ativo && !confirmadoAtivar) {
            abrirConfirmAtivar();
            return;
        }
        if (valores.ativo && valores.avaliacao_automatica && !config.avaliacao_automatica
            && !window.confirm("Com o sistema ATIVO, a avaliação automática semanal PUBLICA preços no ML e no site toda semana. Ligar?")) return;
        if (valores.ativo && !!valores.liquidar_substitui_queima !== !!config.liquidar_substitui_queima
            && !window.confirm("Com o sistema ATIVO, a troca entre queima fixa de 25% e a escada do Liquidar muda o preço do estoque físico na próxima sincronização. Salvar?")) return;
        setSalvando(true);
        try {
            await precificacaoSaveConfig(montarPayload());
            toast({ title: "Configurações salvas!", status: "success", duration: 3000 });
            setInicial(JSON.stringify(valores));
            onSalvo && onSalvo();
            abrirReavaliar();
        } catch (e) {
            const msg = e.response?.data?.error
                || (e.response?.data && typeof e.response.data === "object" ? JSON.stringify(e.response.data) : null);
            toast({ title: "Erro ao salvar", description: msg, status: "error", duration: 6000 });
        }
        setSalvando(false);
        fecharConfirmAtivar();
    };

    const dispararReavaliacao = async (oficial = false) => {
        if (oficial && !window.confirm("Execução OFICIAL do catálogo inteiro: os preços serão PUBLICADOS no ML e no site. Continuar?")) return;
        setDisparandoReavaliacao(true);
        try {
            const r = await precificacaoDispatchExecucao({ simulacao: !oficial, filtros: {} });
            toast({
                title: `Reavaliação #${r.data.execucao_id} disparada`,
                description: "Acompanhe na aba Simulação / Execuções.",
                status: "success", duration: 4000,
            });
        } catch (e) {
            toast({ title: "Erro ao disparar reavaliação", description: e.response?.data?.error, status: "error", duration: 5000 });
        }
        setDisparandoReavaliacao(false);
        fecharReavaliar();
    };

    const canaisAtuais = valores.canais || CANAIS;

    return (
        <Box>
            {/* Master switch */}
            <Box bg="white" borderRadius="md" boxShadow="sm" p={4} mb={4}
                borderLeft="4px solid" borderColor={valores.ativo ? "red.400" : "green.400"}>
                <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
                    <Box>
                        <HStack mb={1}>
                            <Text fontWeight="bold" fontSize="sm">Sistema de precificação inteligente</Text>
                            <Badge colorScheme={valores.ativo ? "red" : "green"}>
                                {valores.ativo ? "ATIVO — altera preços" : "DESATIVADO — só dry-run"}
                            </Badge>
                        </HStack>
                        <Text fontSize="xs" color="gray.600" maxW="640px">
                            Desligado, o motor apenas classifica e simula: <b>nenhum preço muda em nenhum canal</b>.
                            Ligado, as execuções oficiais (manual, semanal automática e por evento) aplicam a MC vigente
                            nos anúncios ML (por conta) e no site (no sync diário das 14:25), sempre respeitando promoções ativas e o piso de MC.
                        </Text>
                    </Box>
                    <HStack>
                        <Text fontSize="xs" color="gray.500">Ativar</Text>
                        <Switch colorScheme="red" isChecked={!!valores.ativo}
                            onChange={e => setValores(v => ({ ...v, ativo: e.target.checked }))} />
                    </HStack>
                </Flex>
                <Divider my={3} />
                <HStack spacing={4} wrap="wrap">
                    <Text fontSize="xs" fontWeight="semibold">Canais avaliados:</Text>
                    {CANAIS.map(c => (
                        <Checkbox key={c} size="sm" isChecked={canaisAtuais.includes(c)}
                            isDisabled={canaisAtuais.length === 1 && canaisAtuais.includes(c)}
                            onChange={e => {
                                setValores(v => ({
                                    ...v,
                                    canais: e.target.checked
                                        ? [...(v.canais || []), c]
                                        : (v.canais || []).filter(x => x !== c),
                                }));
                            }}>
                            <Text fontSize="xs">{CANAL_LABEL[c]}</Text>
                        </Checkbox>
                    ))}
                </HStack>
            </Box>

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                {SECOES_CONFIG.map(sec => (
                    <Box key={sec.titulo} bg="white" borderRadius="md" boxShadow="sm" p={4}>
                        <Text fontSize="sm" fontWeight="semibold" mb={3}>{sec.titulo}</Text>
                        <SimpleGrid columns={{ base: 1, sm: 2 }} spacingX={4} spacingY={3}>
                            {sec.campos.map(campo => (
                                <FormControl key={campo.key}>
                                    <FormLabel fontSize="xs" mb={1} color={campo.destaque ? "red.600" : undefined}>
                                        {campo.label}
                                        {campo.hint && (
                                            <Tooltip label={campo.hint} hasArrow fontSize="xs">
                                                <InfoOutlineIcon ml={1} boxSize={3} color="gray.400" />
                                            </Tooltip>
                                        )}
                                    </FormLabel>
                                    {campo.tipo === "bool" ? (
                                        <Switch size="sm" colorScheme={campo.destaque ? "red" : "orange"}
                                            isChecked={!!valores[campo.key]}
                                            onChange={e => setCampo(campo, e.target.checked)} />
                                    ) : campo.tipo === "dia" ? (
                                        <Select size="sm" value={valores[campo.key] ?? 0}
                                            onChange={e => setCampo(campo, e.target.value)}>
                                            {DIAS_SEMANA.map((d, i) => <option key={i} value={i}>{d}</option>)}
                                        </Select>
                                    ) : (
                                        <Input size="sm" type="number" step="any"
                                            value={displayValor(campo)}
                                            onChange={e => setCampo(campo, e.target.value)} />
                                    )}
                                </FormControl>
                            ))}
                        </SimpleGrid>
                    </Box>
                ))}
            </SimpleGrid>

            <Flex justify="flex-end" mt={4}>
                <Button colorScheme="green" size="sm" leftIcon={<FaSave />}
                    isDisabled={!hasChanges} isLoading={salvando} onClick={() => salvar(false)}>
                    Salvar configurações
                </Button>
            </Flex>

            {/* Confirmação de ativação */}
            <Modal isOpen={confirmAtivarOpen} onClose={fecharConfirmAtivar} isCentered>
                <ModalOverlay />
                <ModalContent mx={3}>
                    <ModalHeader fontSize="md" color="red.600">⚠️ Ativar o sistema?</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Alert status="error" borderRadius="md" mb={3}>
                            <AlertIcon />
                            <Box fontSize="sm">
                                <AlertTitle>Isso passa a ALTERAR PREÇOS de verdade.</AlertTitle>
                                <AlertDescription>
                                    Anúncios do Mercado Livre (por conta) e o preço do site passarão a seguir a MC
                                    vigente calculada pelo motor — a partir da primeira execução oficial. Promoções
                                    ativas continuam respeitadas. Recomendado só depois de validar bastante as simulações.
                                </AlertDescription>
                            </Box>
                        </Alert>
                    </ModalBody>
                    <ModalFooter>
                        <Button size="sm" variant="ghost" mr={3}
                            onClick={() => { setValores(v => ({ ...v, ativo: !!config.ativo })); fecharConfirmAtivar(); }}>
                            Voltar
                        </Button>
                        <Button size="sm" colorScheme="red" isLoading={salvando} onClick={() => salvar(true)}>
                            Sim, ativar e salvar
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Reavaliar após salvar */}
            <Modal isOpen={reavaliarOpen} onClose={fecharReavaliar} isCentered>
                <ModalOverlay />
                <ModalContent mx={3}>
                    <ModalHeader fontSize="md">Rodar reavaliação agora?</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text fontSize="sm" color="gray.700">
                            As configurações mudaram. Quer rodar uma <b>simulação</b> do catálogo inteiro para ver
                            a classificação e os preços com as novas premissas?
                        </Text>
                        <Alert status={valores.ativo ? "warning" : "info"} borderRadius="md" mt={3} py={2} fontSize="xs">
                            <AlertIcon boxSize={4} />
                            <AlertDescription>
                                O catálogo completo leva alguns minutos (~3 min a cada 60 mil produtos).
                                Dá para acompanhar e cancelar na aba Simulação. A simulação não altera nenhum preço.
                                {valores.ativo && " Com o sistema ativo, a execução oficial publica os preços."}
                            </AlertDescription>
                        </Alert>
                    </ModalBody>
                    <ModalFooter flexWrap="wrap" gap={2}>
                        <Button size="sm" variant="ghost" onClick={fecharReavaliar}>Agora não</Button>
                        {valores.ativo && (
                            <Button size="sm" colorScheme="red" variant="outline" isLoading={disparandoReavaliacao}
                                onClick={() => dispararReavaliacao(true)}>
                                Oficial (aplica preços)
                            </Button>
                        )}
                        <Button size="sm" colorScheme="orange" isLoading={disparandoReavaliacao} onClick={() => dispararReavaliacao(false)}>
                            Rodar simulação
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

// ─────────────────────────── Aba: Execuções ───────────────────────────

const ExecucoesTab = ({ aoSelecionar }) => {
    const [dados, setDados] = useState(null);
    const [pagina, setPagina] = useState(1);
    const [carregando, setCarregando] = useState(true);
    const intervalRef = useRef(null);

    const carregar = useCallback(async (pag = 1, silencioso = false) => {
        if (!silencioso) setCarregando(true);
        try {
            const r = await precificacaoListarExecucoes({ page: pag, per_page: 15 });
            setDados(r.data);
            setPagina(pag);
        } catch { /* silencioso */ }
        if (!silencioso) setCarregando(false);
    }, []);

    useEffect(() => { carregar(1); }, [carregar]);

    useEffect(() => {
        const temAtiva = (dados?.data || []).some(e => ["pendente", "em_processamento"].includes(e.status));
        if (temAtiva && !intervalRef.current) {
            intervalRef.current = setInterval(() => carregar(pagina, true), 5000);
        } else if (!temAtiva && intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        return () => { if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; } };
    }, [dados, pagina, carregar]);

    if (carregando && !dados) return <Flex justify="center" py={10}><Spinner color="orange.500" /></Flex>;

    return (
        <Box>
            <Flex justify="flex-end" mb={2}>
                <IconButton size="xs" icon={<RepeatIcon />} aria-label="Atualizar" onClick={() => carregar(pagina)} />
            </Flex>
            <TableContainer bg="white" borderRadius="md" boxShadow="sm">
                <Table size="sm">
                    <Thead>
                        <Tr>
                            <Th></Th><Th>#</Th><Th>Tipo</Th><Th>Modo</Th><Th>Status</Th>
                            <Th isNumeric>Produtos</Th><Th isNumeric>Δ nível</Th><Th isNumeric>Δ preço</Th>
                            <Th isNumeric>Aplicados</Th><Th>Usuário</Th><Th>Quando</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {(dados?.data || []).map(e => {
                            const st = STATUS_EXEC[e.status] || { label: e.status, color: "gray" };
                            const rodando = ["pendente", "em_processamento"].includes(e.status);
                            const pct = e.total_itens > 0 ? Math.round((e.itens_processados / e.total_itens) * 100) : 0;
                            return (
                                <Tr key={e.id}>
                                    <Td>
                                        <Button size="xs" variant="outline" onClick={() => aoSelecionar(e.id)}>Ver</Button>
                                    </Td>
                                    <Td fontSize="xs">{e.id}</Td>
                                    <Td fontSize="xs">{e.tipo}</Td>
                                    <Td>
                                        <Badge fontSize="9px" colorScheme={e.aplicar ? "red" : (e.simulacao ? "purple" : "blue")}>
                                            {e.aplicar ? "aplica" : (e.simulacao ? "simulação" : "oficial")}
                                        </Badge>
                                    </Td>
                                    <Td>
                                        <Badge colorScheme={st.color} fontSize="10px">{st.label}</Badge>
                                        {rodando && <Text fontSize="10px" color="blue.600">{pct}%</Text>}
                                    </Td>
                                    <Td isNumeric fontSize="xs">{fmtInt(e.total_itens)}</Td>
                                    <Td isNumeric fontSize="xs">{fmtInt(e.itens_mudanca_nivel)}</Td>
                                    <Td isNumeric fontSize="xs">{fmtInt(e.itens_mudanca_preco)}</Td>
                                    <Td isNumeric fontSize="xs">{fmtInt(e.itens_aplicados)}</Td>
                                    <Td fontSize="xs">{e.usuario?.name || "sistema"}</Td>
                                    <Td fontSize="xs">{e.created_at ? new Date(e.created_at).toLocaleString("pt-BR") : "—"}</Td>
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>
            </TableContainer>
            {dados && (
                <Flex justify="space-between" align="center" mt={2}>
                    <Text fontSize="xs" color="gray.500">
                        {fmtInt(dados.total)} execuções · página {dados.current_page} de {dados.last_page}
                    </Text>
                    <HStack>
                        <Button size="xs" isDisabled={pagina <= 1} onClick={() => carregar(pagina - 1)}>Anterior</Button>
                        <Button size="xs" isDisabled={pagina >= (dados.last_page || 1)} onClick={() => carregar(pagina + 1)}>Próxima</Button>
                    </HStack>
                </Flex>
            )}
        </Box>
    );
};

// ─────────────────────────── Página ───────────────────────────

const PrecificacaoInteligente = () => {
    const toast = useToast();
    const [dashboard, setDashboard] = useState(null);
    const [config, setConfig] = useState(null);
    const [tabIndex, setTabIndex] = useState(0);
    const [execucaoSelecionada, setExecucaoSelecionada] = useState(null);

    const carregarTudo = useCallback(async () => {
        try {
            const [d, c] = await Promise.all([precificacaoDashboard(), precificacaoGetConfig()]);
            setDashboard(d.data);
            setConfig(c.data);
            const padrao = d.data.em_andamento || d.data.ultima_execucao || d.data.ultima_oficial;
            if (padrao) setExecucaoSelecionada(prev => prev || padrao.id);
        } catch (e) {
            toast({ title: "Erro ao carregar precificação inteligente", status: "error", duration: 4000 });
        }
    }, [toast]);

    useEffect(() => { carregarTudo(); }, [carregarTudo]);

    const ativo = config?.ativo;

    return (
        <Box ml={{ base: 0, md: "64px" }} pt="56px" minH="100vh" bg="gray.50">
            <ScrollToTop />
            <Box maxW="1280px" mx="auto" py={{ base: 4, md: 6 }} px={{ base: 3, md: 8 }}>
                <Flex justify="space-between" align={{ base: "flex-start", md: "center" }}
                    mb={4} direction={{ base: "column", md: "row" }} gap={2}>
                    <Box>
                        <HStack mb={1}>
                            <TrendingUp size={20} color={ACCENT} />
                            <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold">Precificação Inteligente</Text>
                            {config && (
                                <Badge colorScheme={ativo ? "red" : "green"} fontSize="xs">
                                    {ativo ? "ATIVO" : "DESATIVADO (dry-run)"}
                                </Badge>
                            )}
                        </HStack>
                        <Text fontSize="sm" color="gray.600">
                            Sistema de níveis: demanda × posição de mercado → ação de MC por produto e canal.
                        </Text>
                    </Box>
                </Flex>

                {["falhou", "cancelado"].includes(dashboard?.ultima_execucao?.status) && (
                    <Alert status="warning" borderRadius="md" mb={4} fontSize="sm">
                        <AlertIcon />
                        <AlertDescription>
                            A última execução (#{dashboard.ultima_execucao.id}, {dashboard.ultima_execucao.simulacao ? "simulação" : "oficial"})
                            {" "}{dashboard.ultima_execucao.status === "falhou" ? "falhou" : "foi cancelada"} em {fmtData(dashboard.ultima_execucao.finalizado_em)}:
                            {" "}{Number(dashboard.ultima_execucao.itens_aplicados) > 0
                                ? `os ${fmtInt(dashboard.ultima_execucao.itens_aplicados)} preços publicados antes da interrupção seguem no ar; o resto dela não vale.`
                                : "nenhum preço ou nível dela vale."}
                            {dashboard.ultima_execucao.erro_msg && <Text as="span" fontSize="xs" color="gray.600"> Motivo: {dashboard.ultima_execucao.erro_msg}</Text>}
                        </AlertDescription>
                    </Alert>
                )}

                {config && !ativo && (
                    <Alert status="success" borderRadius="md" mb={4} fontSize="sm">
                        <AlertIcon />
                        <AlertDescription>
                            Modo seguro: o sistema está <b>desativado</b> — nenhuma execução altera preço em nenhum canal.
                            Use as simulações para validar as regras antes de qualquer ativação.
                        </AlertDescription>
                    </Alert>
                )}

                <Tabs variant="enclosed-colored" isLazy index={tabIndex} onChange={setTabIndex}>
                    <TabList flexWrap="wrap">
                        <Tab fontSize="sm">Visão Geral</Tab>
                        <Tab fontSize="sm">Simulação (Dry-run)</Tab>
                        <Tab fontSize="sm">Configurações</Tab>
                        <Tab fontSize="sm">Execuções</Tab>
                    </TabList>
                    <TabPanels bg="transparent">
                        <TabPanel px={0}>
                            <VisaoGeralTab dashboard={dashboard} onRefresh={carregarTudo} />
                        </TabPanel>
                        <TabPanel px={0}>
                            <SimulacaoTab
                                config={config}
                                execucaoSelecionada={execucaoSelecionada}
                                setExecucaoSelecionada={setExecucaoSelecionada}
                                onExecucaoDisparada={carregarTudo}
                            />
                        </TabPanel>
                        <TabPanel px={0}>
                            <ConfiguracoesTab config={config} onSalvo={carregarTudo} />
                        </TabPanel>
                        <TabPanel px={0}>
                            <ExecucoesTab aoSelecionar={(id) => { setExecucaoSelecionada(id); setTabIndex(1); }} />
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </Box>
        </Box>
    );
};

export default PrecificacaoInteligente;
